/**
 * Onde carregar uma imagem de mídia (foto de perfil, thumbnail de post). Veio
 * de `apps/web/src/lib/resolve-media-image-url.ts`, sem `process.env`: o
 * bucket e o proxy entram por parâmetro, porque cada plataforma tem o seu.
 *
 * - Imagem pública no S3 da Tãmi carrega direto.
 * - Imagem de fora (CDN do Instagram, do TikTok) passa pelo proxy quando há um:
 *   no navegador (web e desktop) o hotlink é bloqueado. No app nativo não há
 *   proxy (`proxyPath: null`) e a imagem carrega direto.
 */

import type { ResolveMediaImageUrlOptions } from "./media-image-url.types";

export const TAMI_S3_BUCKET_NAMES = ["tami-prod", "tami-stage"] as const;

export const TAMI_PUBLIC_IMAGE_PATH_PREFIXES = [
  "tami/posts/",
  "tami/profiles/",
  "profiles/",
  "migrations/clerk-profile-images/",
] as const;

const LEGACY_S3_BUCKET_NAMES = ["john-storage-public", "timeazy-stage"] as const;

const S3_PUBLIC_URL_PATTERN = /^https:\/\/([^.]+)\.s3\.[a-z0-9-]+\.amazonaws\.com\/(.+)$/i;

function isPublicTamiImageKey(key: string): boolean {
  return TAMI_PUBLIC_IMAGE_PATH_PREFIXES.some((prefix) => {
    if (prefix.endsWith("/")) {
      return key.startsWith(prefix);
    }
    return key === prefix || key.startsWith(`${prefix}/`);
  });
}

function decodeS3ObjectKey(rawKey: string): string {
  try {
    return decodeURIComponent(rawKey);
  } catch {
    return rawKey;
  }
}

/** Reescreve host de buckets legados para o bucket Tami atual (mesma key no S3). */
export function normalizeLegacyTamiS3ImageUrl(url: string, targetBucket: string): string {
  const trimmed = url.trim();
  const match = trimmed.match(S3_PUBLIC_URL_PATTERN);
  if (!match?.[1]) {
    return trimmed;
  }
  const bucket = match[1];
  if (!LEGACY_S3_BUCKET_NAMES.includes(bucket as (typeof LEGACY_S3_BUCKET_NAMES)[number])) {
    return trimmed;
  }
  return trimmed.replace(`${bucket}.s3.`, `${targetBucket}.s3.`);
}

export function parseTamiS3PublicImageUrl(
  url: string,
  targetBucket: string,
): { bucket: string; key: string } | null {
  const normalized = normalizeLegacyTamiS3ImageUrl(url, targetBucket);
  const match = normalized.match(S3_PUBLIC_URL_PATTERN);
  if (!match?.[1] || !match[2]) {
    return null;
  }
  const bucket = match[1];
  if (!TAMI_S3_BUCKET_NAMES.includes(bucket as (typeof TAMI_S3_BUCKET_NAMES)[number])) {
    return null;
  }
  const rawKey = match[2].split("?")[0]?.trim() ?? "";
  if (!rawKey) {
    return null;
  }
  const key = decodeS3ObjectKey(rawKey);
  if (!isPublicTamiImageKey(key)) {
    return null;
  }
  return { bucket, key };
}

export function isDirectPublicTamiImageUrl(
  url: string | null | undefined,
  targetBucket: string,
): boolean {
  return Boolean(url && parseTamiS3PublicImageUrl(url, targetBucket));
}

function viaProxy(url: string, proxyPath: string | null): string {
  return proxyPath ? `${proxyPath}?url=${encodeURIComponent(url)}` : url;
}

export function resolveMediaImageUrl(
  url: string | null | undefined,
  options: ResolveMediaImageUrlOptions,
): string | null {
  const trimmed = url?.trim();
  if (!trimmed) {
    return null;
  }
  if (trimmed.startsWith("/") || trimmed.startsWith("data:")) {
    return trimmed;
  }

  const normalized = normalizeLegacyTamiS3ImageUrl(trimmed, options.targetBucket);
  const isHttp = normalized.startsWith("http://") || normalized.startsWith("https://");

  if (options.forCanvas) {
    return isHttp ? viaProxy(normalized, options.proxyPath) : normalized;
  }
  if (isDirectPublicTamiImageUrl(normalized, options.targetBucket)) {
    return normalized;
  }
  return isHttp ? viaProxy(normalized, options.proxyPath) : normalized;
}
